// src/pages/logs/index.jsx
import React, { useState, useEffect } from 'react';
import api from '../../core/api';
import '../factors/Factors.css';
import './Logs.css';
import JalaliDatePicker from '../../components/JalaliDatePicker';

const FA = '۰۱۲۳۴۵۶۷۸۹';
const toFa = (v) => String(v ?? '').replace(/\d/g, (d) => FA[+d]);

const ACTIONS = {
  create: 'ساخت',
  update: 'ویرایش',
  delete: 'حذف',
  login: 'ورود',
  logout: 'خروج',
  upload: 'آپلود',
};
const ENTITIES = {
  factor: 'فاکتور',
  customer: 'مشتری',
  product: 'محصول',
  transaction: 'تراکنش',
  letter: 'نامه',
  company: 'تنظیمات شرکت',
  staff: 'پرسنل',
  auth: 'حساب کاربری',
};
const FIELDS = {
  name: 'نام', national: 'شماره ملی/ثبت', eco: 'شماره اقتصادی', postal: 'کد پستی',
  province: 'استان', city: 'شهرستان', address: 'نشانی', phone: 'تلفن',
  code: 'کد', description: 'توضیحات', unit: 'واحد', price: 'مبلغ',
};

const EMPTY_FILTERS = { actorId: '', entity: '', action: '', from: '', to: '', q: '' };

export default function Logs() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const setF = (patch) => {
    setFilters((p) => ({ ...p, ...patch }));
    setPage(1);
  };

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const params = { page };
        Object.entries(filters).forEach(([k, v]) => v && (params[k] = v));
        const res = await api.get('/logs', { params });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'خطا در دریافت گزارش فعالیت‌ها');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [filters, page]);

  const logs = data?.logs || [];
  const actors = data?.actors || [];

  return (
    <>
      <div className="card">
        <h2>گزارش فعالیت‌ها</h2>
        <div className="grid">
          <div>
            <label>کاربر</label>
            <select value={filters.actorId} onChange={(e) => setF({ actorId: e.target.value })}>
              <option value="">همه</option>
              {actors.map((a) => (
                <option key={a.actor_id} value={a.actor_id}>
                  {(a.actor_name || a.actor_phone) + (a.actor_role === 'owner' ? ' (مالک)' : '')}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>بخش</label>
            <select value={filters.entity} onChange={(e) => setF({ entity: e.target.value })}>
              <option value="">همه</option>
              {Object.entries(ENTITIES).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label>نوع عملیات</label>
            <select value={filters.action} onChange={(e) => setF({ action: e.target.value })}>
              <option value="">همه</option>
              {Object.entries(ACTIONS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label>جستجو در عنوان</label>
            <input
              autoComplete="off"
              value={filters.q}
              onChange={(e) => setF({ q: e.target.value })}
              placeholder="نام مشتری، شماره فاکتور، ..."
            />
          </div>
          <div>
            <label>از تاریخ</label>
            <JalaliDatePicker value={filters.from} onChange={(v) => setF({ from: v })} placeholder="از تاریخ" name="from" />
          </div>
          <div>
            <label>تا تاریخ</label>
            <JalaliDatePicker value={filters.to} onChange={(v) => setF({ to: v })} placeholder="تا تاریخ" name="to" />
          </div>
        </div>
        <div className="flexend">
          <button type="button" className="btn secondary small" onClick={() => { setFilters(EMPTY_FILTERS); setPage(1); }}>
            <i className="fa-solid fa-filter-circle-xmark" /> پاک کردن فیلترها
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header-row">
          <h2>فعالیت‌ها</h2>
          {data && <span className="muted">{(data.total || 0).toLocaleString('fa-IR')} مورد</span>}
        </div>

        {error && <div className="empty">{error}</div>}
        {!error && loading && !data && <div className="empty">در حال بارگذاری...</div>}
        {!error && data && logs.length === 0 && <div className="empty">فعالیتی پیدا نشد.</div>}

        <div style={{ opacity: loading && data ? 0.6 : 1, transition: 'opacity .15s' }}>
          {logs.map((l) => {
            const changes = l.details?.changes || [];
            return (
              <div className="list-item" key={l.id}>
                <div className="al-main">
                  <div>
                    <span className={`al-act ${l.action}`}>{ACTIONS[l.action] || l.action}</span>{' '}
                    <b>{ENTITIES[l.entity] || l.entity}</b>
                    {l.entity_label && <span className="muted"> — {l.entity_label}</span>}
                  </div>

                  <div className="muted" style={{ marginTop: 4 }}>
                    <i className="fa-solid fa-user" /> {l.actor_name || l.actor_phone}{' '}
                    <span className="badge">{l.actor_role === 'owner' ? 'مالک' : 'پرسنل'}</span>
                    {l.ip_address && <span style={{ direction: 'ltr', display: 'inline-block', marginInlineStart: 8 }}>{l.ip_address}</span>}
                  </div>

                  {changes.length > 0 && (
                    <ul className="al-changes">
                      {changes.map((c, i) => (
                        <li key={i}>
                          <b>{FIELDS[c.field] || c.field}:</b>{' '}
                          <span className="al-from">{c.from || '-'}</span>
                          {' ← '}
                          <span className="al-to">{c.to || '-'}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="al-time">
                  <span className="badge">{toFa(l.log_date)}</span>{' '}
                  <span className="badge">{toFa(l.log_time)}</span>
                </div>
              </div>
            );
          })}
        </div>

        {data && data.pages > 1 && (
          <div className="flexend" style={{ alignItems: 'center' }}>
            <button className="btn secondary small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <i className="fa-solid fa-chevron-right" /> قبلی
            </button>
            <span className="muted">
              صفحه {page.toLocaleString('fa-IR')} از {data.pages.toLocaleString('fa-IR')}
            </span>
            <button className="btn secondary small" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}>
              بعدی <i className="fa-solid fa-chevron-left" />
            </button>
          </div>
        )}
      </div>
    </>
  );
}